import { useSyncExternalStore } from 'react';
import { ordersAPI } from '../services/api';
import { getStoredUser } from './auth';
import { compressImage, ensureImageExtension, getImageValidationError } from './imageCompress';

/**
 * Damage photos upload in the background for the whole app: a photo taken on the order card or in
 * "Мой день" keeps uploading while staff move on. One file at a time: a phone has little memory for
 * compression and one upload channel.
 */

export const DAMAGE_PHOTOS_EVENT = 'orderDamagePhotosUpdated';

let items = [];
let nextId = 1;
let pumping = false;
const listeners = new Set();

const emit = () => listeners.forEach((listener) => listener());

const setItems = (next) => {
    items = next;
    emit();
};

const patch = (id, changes) => setItems(items.map((item) => (item.id === id ? { ...item, ...changes } : item)));

const remove = (id) => {
    const target = items.find((item) => item.id === id);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    setItems(items.filter((item) => item.id !== id));
};

// Подменяется в тестах
let uploader = (orderId, file, options) => ordersAPI.uploadDamagePhotoFile(orderId, file, options);
let compressor = async (file) => ensureImageExtension(await compressImage(file));

export const setDamagePhotoUploaderForTests = (upload, compress) => {
    uploader = upload;
    compressor = compress;
};

// Повтор не поможет: формат, размер или права решены сервером окончательно
const FINAL_UPLOAD_STATUSES = [400, 403, 404, 413, 415, 422];

const currentUserId = () => getStoredUser()?.id ?? null;

const processItem = async (item) => {
    let prepared = item.prepared;
    // На общем телефоне фото одного мастера не уходят под аккаунтом другого
    if (item.userId !== currentUserId()) {
        patch(item.id, { status: 'error', message: 'Фото снято под другим пользователем', canRetry: false });
        return;
    }
    try {
        if (!prepared) {
            patch(item.id, { status: 'preparing' });
            prepared = await compressor(item.file);
            patch(item.id, { prepared });
        }
        const validationError = getImageValidationError(prepared);
        if (validationError) {
            patch(item.id, { status: 'error', message: validationError, canRetry: false });
            return;
        }
        patch(item.id, { status: 'uploading', progress: 0, message: '' });
        const order = await uploader(item.orderId, prepared, {
            onProgress: (progress) => patch(item.id, { progress }),
        });
        window.dispatchEvent(new CustomEvent(DAMAGE_PHOTOS_EVENT, { detail: { orderId: item.orderId, order } }));
        remove(item.id);
    } catch (err) {
        patch(item.id, {
            status: 'error',
            message: err?.message || 'Не удалось загрузить фото',
            canRetry: !FINAL_UPLOAD_STATUSES.includes(err?.status),
        });
    }
};

const pump = async () => {
    if (pumping) return;
    pumping = true;
    try {
        let next = items.find((item) => item.status === 'queued');
        while (next) {
            // eslint-disable-next-line no-await-in-loop
            await processItem(next);
            next = items.find((item) => item.status === 'queued');
        }
    } finally {
        pumping = false;
    }
};

export const enqueueDamagePhotos = (orderId, files) => {
    const selected = Array.from(files || []);
    if (!orderId || !selected.length) return;
    setItems([
        ...items,
        ...selected.map((file) => ({
            id: nextId++,
            orderId: Number(orderId),
            userId: currentUserId(),
            file,
            previewUrl: URL.createObjectURL(file),
            status: 'queued',
            progress: 0,
            message: '',
            canRetry: true,
        })),
    ]);
    return pump();
};

export const retryDamagePhoto = (id) => {
    patch(id, { status: 'queued', message: '', progress: 0 });
    return pump();
};

export const retryAllDamagePhotos = () => {
    setItems(items.map((item) => (item.status === 'error' && item.canRetry ? { ...item, status: 'queued', message: '' } : item)));
    return pump();
};

export const dismissDamagePhoto = (id) => remove(id);

export const getDamagePhotoItems = () => items;

const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

/** Photos in the queue, optionally only those of one order. */
export const useDamagePhotoQueue = (orderId = null) => {
    const all = useSyncExternalStore(subscribe, getDamagePhotoItems);
    return orderId ? all.filter((item) => item.orderId === Number(orderId)) : all;
};

// Не загруженные, но ещё повторяемые фото тоже пропадут при закрытии вкладки
export const hasPendingDamagePhotos = () => items.some((item) => item.status !== 'error' || item.canRetry);

// Закрытие вкладки во время загрузки потеряет фото: браузер спросит подтверждение
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', (event) => {
        if (!hasPendingDamagePhotos()) return;
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = '';
    });
}
